/**
 * Catálogos globais — sistemas, nomes de sistema e fabricantes.
 *
 * **Não são do projeto: são do sistema inteiro.** É a diferença que importa
 * aqui e que a tela precisa deixar clara o tempo todo — renomear um sistema
 * muda o nome dele em todos os projetos que o usam, e apagar um que está em uso
 * é o tipo de coisa que só aparece depois, no conteúdo publicado.
 *
 * Por isso ela mora no menu do topo, e não numa aba de projeto, e por isso a
 * exclusão pede confirmação com o aviso do alcance.
 *
 * As três abas têm a mesma forma — nome e descrição — porque as três tabelas
 * têm. Sistemas tem dois campos a mais, de publicação no site do cliente.
 */

import { useMemo, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { api, foraDoProjeto, obter } from "@/api/cliente"
import { ErroDaApi, ErroDeTrava } from "@/api/erros"
import { LinhaDeAviso, Tabela, type Coluna } from "@/kit/Tabela"
import { BarraDeEstado } from "@/kit/BarraDeEstado"
import { AvisoDeDadoGlobal } from "@/kit/AvisoDeDadoGlobal"
import { POR_PAGINA } from "@/api/query"
import {
  ordenarLista, SeletorDeOrdem, usarOrdemDaLista, usarRecemIncluido,
} from "@/kit/ordemDaLista"

type Registro = {
  id: number
  nome: string
  descricao: string
  nomeMkt?: string | null
  url?: string | null
  baseId?: number
}

type Aba = "sistemas" | "nome-sistemas" | "fab-sistemas"

const ABAS: { chave: Aba; rotulo: string; singular: string; ajuda: string }[] = [
  {
    chave: "sistemas", rotulo: "Sistemas", singular: "sistema",
    ajuda: "O sistema do veículo — injeção, ar-condicionado, ABS. Usado pelo diagrama e pelo site.",
  },
  {
    chave: "nome-sistemas", rotulo: "Nomes de sistema", singular: "nome de sistema",
    ajuda: "O nome comercial do sistema — IAW 4SF, ME 7.5. É por ele que o conector é catalogado.",
  },
  {
    chave: "fab-sistemas", rotulo: "Fabricantes", singular: "fabricante",
    ajuda: "Quem fabrica o sistema — Bosch, Magneti Marelli, Delphi.",
  },
]

export function CatalogosGlobais() {
  const qc = useQueryClient()
  const [aba, setAba] = useState<Aba>("sistemas")
  const [busca, setBusca] = useState("")
  const [selecionado, setSelecionado] = useState<Registro | null>(null)
  const [incluindo, setIncluindo] = useState(false)
  const [recado, setRecado] = useState<string | null>(null)
  const [ordem, setOrdem] = usarOrdemDaLista("catalogos-globais")
  const recente = usarRecemIncluido()

  const esquema = ABAS.find((a) => a.chave === aba)!

  const lista = useQuery({
    queryKey: ["catalogo-global", aba] as const,
    queryFn: () => obter<Registro[]>(`api/${aba}`, {}),
  })

  const salvar = useMutation({
    // O POST devolve o registro criado; é o id dele que a lista realça.
    mutationFn: async ({ id, dados }: { id: number | null; dados: Partial<Registro> }) => {
      if (id) { await api.put(`api/${aba}/${id}`, dados, foraDoProjeto()); return null }
      const r = await api.post(`api/${aba}`, dados, foraDoProjeto())
      return (r.data ?? null) as Registro | null
    },
    onSuccess: (criado, { id, dados }) => {
      qc.invalidateQueries({ queryKey: ["catalogo-global", aba] })
      qc.invalidateQueries({ queryKey: ["catalogo"] })
      setIncluindo(false)
      if (id) { setRecado("Alteração gravada."); return }
      // Um filtro digitado antes poderia esconder justamente o que acabou de entrar.
      setBusca("")
      recente.marcar(criado?.id)
      const nome = criado?.nome ?? dados.nome ?? ""
      setRecado(`${maiuscula(esquema.singular)} “${nome}” incluído${criado?.id ? ` — id ${criado.id}` : ""}.`)
    },
  })

  const excluir = useMutation({
    mutationFn: async (id: number) => { await api.delete(`api/${aba}/${id}`, foraDoProjeto()) },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["catalogo-global", aba] })
      setSelecionado(null)
    },
  })

  // O registro de id 0 é o vazio do cadastro antigo, não escolha de ninguém.
  const achados = useMemo(() => {
    const todos = (lista.data ?? []).filter((x) => x.id > 0)
    const t = busca.trim().toLowerCase()
    const filtrados = !t ? todos : todos.filter((x) =>
      (x.nome ?? "").toLowerCase().includes(t) || (x.descricao ?? "").toLowerCase().includes(t))
    return ordenarLista(filtrados, ordem, (x) => x.id, (x) => x.nome ?? "")
  }, [lista.data, busca, ordem])

  /**
   * Desenha no máximo 200, como o resto do sistema.
   *
   * São 1.662 sistemas; pôr todos no DOM de uma vez trava a rolagem sem ajudar
   * ninguém — ninguém percorre mil e seiscentos itens com o olho, procura. O
   * filtro acima é em memória e instantâneo, então o caminho é filtrar, e a
   * barra diz quantos ficaram de fora para o corte não passar despercebido.
   */
  const corte = achados.slice(0, POR_PAGINA)
  // Na ordem alfabética o recém-incluído pode cair depois do corte; aí ele sobe para o
  // topo enquanto está realçado, em vez de sumir.
  const fora = recente.id != null && !corte.some((x) => x.id === recente.id)
    ? achados.find((x) => x.id === recente.id) : undefined
  const itens = fora ? [fora, ...corte.slice(0, POR_PAGINA - 1)] : corte
  const escondidos = achados.length - itens.length

  const colunas: Coluna<Registro>[] = [
    { chave: "id", rotulo: "Id", largura: 74, mono: true, alinhamento: "direita", render: (x) => x.id },
    { chave: "nome", rotulo: "Nome", minimo: 240, render: (x) => x.nome },
    {
      chave: "descricao", rotulo: "Descrição", minimo: 240,
      render: (x) => (x.descricao && x.descricao !== x.nome
        ? x.descricao
        : <span style={{ color: "var(--txt3)" }}>—</span>),
    },
  ]

  return (
    <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <AvisoDeDadoGlobal oQueMuda="Renomear ou excluir um destes registros vale para todos os projetos" />

      <div
        className="barra"
        style={{
          height: 38, flex: "0 0 38px", display: "flex", alignItems: "stretch", gap: 2,
          padding: "0 14px", background: "var(--surface2)", borderBottom: "1px solid var(--line2)",
        }}
      >
        {ABAS.map((a) => (
          <button
            key={a.chave}
            onClick={() => {
              setAba(a.chave); setSelecionado(null); setIncluindo(false); setBusca("")
              setRecado(null); recente.marcar(null)
            }}
            aria-pressed={aba === a.chave}
            style={{
              display: "flex", alignItems: "center", padding: "0 12px", fontSize: 12.5,
              fontWeight: aba === a.chave ? 600 : 500,
              color: aba === a.chave ? "var(--txt)" : "var(--txt2)",
              boxShadow: aba === a.chave ? "inset 0 -2px 0 var(--brand)" : "none",
            }}
          >
            {a.rotulo}
          </button>
        ))}
      </div>

      <div
        className="barra"
        style={{
          height: 40, flex: "0 0 40px", display: "flex", alignItems: "center", gap: 10,
          padding: "0 18px", background: "var(--surface)", borderBottom: "1px solid var(--line)",
        }}
      >
        <input
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          placeholder={`Filtrar ${esquema.rotulo.toLowerCase()}`}
          aria-label={`Filtrar ${esquema.rotulo}`}
          // Filtro em memória: a lista inteira já veio numa chamada só, então
          // digitar aqui não custa banco. É o contrário do filtro da grade.
          style={{
            width: 260, height: 28, padding: "0 9px", borderRadius: 7,
            border: "1px solid var(--line2)", background: "var(--surface2)", fontSize: 12.5,
          }}
        />
        <SeletorDeOrdem valor={ordem} aoMudar={setOrdem} />
        <span style={{ fontSize: 11.5, color: "var(--txt3)", maxWidth: "52ch", lineHeight: 1.45 }}>
          {esquema.ajuda}
        </span>
        <span style={{ marginLeft: "auto" }} />
        <button
          onClick={() => { setSelecionado(null); setIncluindo(true); setRecado(null) }}
          style={{
            height: 28, padding: "0 11px", borderRadius: 7, background: "var(--brand)",
            color: "#FFFFFF", fontSize: 12.5, fontWeight: 600,
          }}
        >
          Novo {esquema.singular}
        </button>
      </div>

      <div style={{ flex: 1, minHeight: 0, display: "flex" }}>
        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
          <Tabela<Registro>
            colunas={colunas}
            itens={itens}
            chaveDe={(x) => x.id}
            selecionada={selecionado?.id ?? null}
            recente={recente.id}
            aoSelecionar={(x) => { setIncluindo(false); setSelecionado(x) }}
            vazio={
              lista.isPending ? <LinhaDeAviso>Carregando…</LinhaDeAviso>
              : lista.error ? (
                <LinhaDeAviso>
                  <b style={{ color: "var(--erro)" }}>
                    {lista.error instanceof ErroDaApi
                      ? lista.error.message : "Não foi possível falar com o servidor."}
                  </b>
                </LinhaDeAviso>
              ) : busca ? (
                <LinhaDeAviso>Nada com “{busca}”.</LinhaDeAviso>
              ) : <LinhaDeAviso>Nenhum registro.</LinhaDeAviso>
            }
          />
        </div>

        {(incluindo || selecionado) && (
          <Painel
            aba={aba}
            singular={esquema.singular}
            registro={incluindo ? null : selecionado}
            salvando={salvar.isPending}
            excluindo={excluir.isPending}
            erro={salvar.error ?? excluir.error}
            aoSalvar={(dados) => salvar.mutate({ id: selecionado?.id ?? null, dados })}
            aoExcluir={() => selecionado && excluir.mutate(selecionado.id)}
            aoFechar={() => { setIncluindo(false); setSelecionado(null) }}
          />
        )}
      </div>

      <BarraDeEstado>
        <span>
          <b className="num" style={{ color: "var(--txt)" }}>{achados.length.toLocaleString("pt-BR")}</b>{" "}
          {esquema.rotulo.toLowerCase()}
          {escondidos > 0 && (
            <span style={{ color: "var(--txt3)" }}>
              {" · "}mostrando {itens.length} — filtre para achar o resto
            </span>
          )}
        </span>
        {recado ? (
          <span role="status" style={{ color: "var(--ok-on-soft)", fontWeight: 600 }}>{recado}</span>
        ) : (
          <span style={{ color: "var(--acao-on-soft)" }}>
            Isto vale para todos os projetos, não só para um.
          </span>
        )}
      </BarraDeEstado>
    </div>
  )
}

function Painel({
  aba, singular, registro, salvando, excluindo, erro, aoSalvar, aoExcluir, aoFechar,
}: {
  aba: Aba
  singular: string
  registro: Registro | null
  salvando: boolean
  excluindo: boolean
  erro: unknown
  aoSalvar: (dados: Partial<Registro>) => void
  aoExcluir: () => void
  aoFechar: () => void
}) {
  const incluindo = registro == null
  const [nome, setNome] = useState(registro?.nome ?? "")
  const [descricao, setDescricao] = useState(registro?.descricao ?? "")
  const [nomeMkt, setNomeMkt] = useState(registro?.nomeMkt ?? "")
  const [url, setUrl] = useState(registro?.url ?? "")
  const [confirmando, setConfirmando] = useState(false)

  // Trocar de registro recarrega o formulário. `key` no pai faria o mesmo, mas
  // aqui o estado é curto e explícito é melhor que mágico.
  const [ancora, setAncora] = useState(registro?.id ?? 0)
  if ((registro?.id ?? 0) !== ancora) {
    setAncora(registro?.id ?? 0)
    setNome(registro?.nome ?? "")
    setDescricao(registro?.descricao ?? "")
    setNomeMkt(registro?.nomeMkt ?? "")
    setUrl(registro?.url ?? "")
    setConfirmando(false)
  }

  return (
    <aside
      className="gaveta"
      style={{
        flex: "0 0 380px", width: 380, borderLeft: "1px solid var(--line2)",
        background: "var(--surface)", display: "flex", flexDirection: "column", minHeight: 0,
      }}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault()
          aoSalvar(aba === "sistemas"
            ? { nome, descricao, nomeMkt, url, baseId: registro?.baseId ?? 1 }
            : { nome, descricao })
        }}
        onKeyDown={(e) => { if (e.key === "Escape") { e.preventDefault(); aoFechar() } }}
        style={{ height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}
      >
        <div style={{
          flex: "0 0 44px", display: "flex", alignItems: "center", gap: 8,
          padding: "0 14px", borderBottom: "1px solid var(--line)",
        }}>
          <span style={{ flex: 1, minWidth: 0, fontSize: 13.5, fontWeight: 600,
                         overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {incluindo ? `Novo ${singular}` : registro!.nome}
          </span>
          <button type="button" onClick={aoFechar} aria-label="Fechar o painel"
                  style={{ display: "flex", color: "var(--txt3)", padding: 4 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true"
                 style={{ fill: "none", stroke: "currentColor", strokeWidth: 2.2, strokeLinecap: "round" }}>
              <path d="M6 6l12 12M18 6 6 18" />
            </svg>
          </button>
        </div>

        <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: 14,
                      display: "flex", flexDirection: "column", gap: 11 }}>
          <Campo rotulo="Nome">
            <input value={nome} onChange={(e) => setNome(e.target.value)} style={entrada} />
          </Campo>
          <Campo rotulo="Descrição">
            <input value={descricao} onChange={(e) => setDescricao(e.target.value)} style={entrada} />
          </Campo>

          {aba === "sistemas" && (
            <>
              <Campo rotulo="Nome no site">
                <input value={nomeMkt ?? ""} onChange={(e) => setNomeMkt(e.target.value)} style={entrada} />
              </Campo>
              <Campo rotulo="Endereço no site">
                <input value={url ?? ""} onChange={(e) => setUrl(e.target.value)}
                       style={{ ...entrada, fontFamily: "var(--fm)" }} />
              </Campo>
            </>
          )}

          <p style={{
            margin: 0, padding: "9px 11px", borderRadius: 8, fontSize: 11,
            lineHeight: 1.5, background: "var(--acao-soft)", color: "var(--acao-on-soft)",
          }}>
            Este cadastro é <b>global</b>. Renomear muda o nome em todos os projetos que usam este
            registro, e excluir tira de todos de uma vez.
          </p>

          {erro != null && (
            <div style={{
              padding: "10px 12px", borderRadius: 8, fontSize: 11.5, lineHeight: 1.5,
              background: erro instanceof ErroDeTrava ? "var(--acao-soft)" : "var(--erro-soft)",
              color: erro instanceof ErroDeTrava ? "var(--acao-on-soft)" : "var(--erro-on-soft)",
            }}>
              {erro instanceof ErroDaApi || erro instanceof Error ? erro.message : "Não foi possível gravar."}
            </div>
          )}
        </div>

        <div style={{
          flex: "0 0 auto", display: "flex", alignItems: "center", gap: 8,
          padding: 12, borderTop: "1px solid var(--line)",
        }}>
          {!incluindo && (
            confirmando ? (
              <button type="button" onClick={aoExcluir} disabled={excluindo}
                      style={{ height: 32, padding: "0 11px", borderRadius: 7, fontSize: 12.5,
                               fontWeight: 600, background: "var(--erro)", color: "#FFFFFF" }}>
                {excluindo ? "Excluindo…" : "Excluir de todos os projetos"}
              </button>
            ) : (
              <button type="button" onClick={() => setConfirmando(true)}
                      style={{ height: 32, padding: "0 11px", borderRadius: 7, fontSize: 12.5,
                               fontWeight: 600, color: "var(--erro)", border: "1px solid var(--line2)" }}>
                Excluir
              </button>
            )
          )}
          <span style={{ flex: 1 }} />
          <button type="button" onClick={aoFechar}
                  style={{ height: 32, padding: "0 12px", borderRadius: 7, fontSize: 12.5, color: "var(--txt2)" }}>
            Cancelar
          </button>
          <button type="submit" disabled={salvando || nome.trim() === ""}
                  style={{
                    height: 32, padding: "0 13px", borderRadius: 7, background: "var(--brand)",
                    color: "#FFFFFF", fontSize: 12.5, fontWeight: 600,
                    opacity: salvando || nome.trim() === "" ? 0.6 : 1,
                  }}>
            {salvando ? "Salvando…" : incluindo ? "Incluir" : "Salvar"}
          </button>
        </div>
      </form>
    </aside>
  )
}

const entrada: React.CSSProperties = {
  width: "100%", height: 32, padding: "0 9px", borderRadius: 7,
  border: "1px solid var(--line2)", background: "var(--surface2)", fontSize: 12.5,
}

function Campo({ rotulo, children }: { rotulo: string; children: React.ReactNode }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span style={{ fontSize: 11.5, fontWeight: 600, color: "var(--txt2)" }}>{rotulo}</span>
      {children}
    </label>
  )
}

function maiuscula(t: string) {
  return t.charAt(0).toUpperCase() + t.slice(1)
}
